import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { HdOwnLineTextComponent } from '../../../../line-text-review/hd-own-line-text.component';
import { HdGateLineActivationOrigin, HdGateLineDualityViewModel } from './hd-gate-line.view-model';

/** Exaltation (▲) of detriment (▼). */
export type HdGateLineDualityKind = 'exaltation' | 'detriment';

/**
 * Visuele nadruk van een duality-rij a.d.h.v. de fixatie-status van de lijn:
 * - `fixed`   → deze pool is gefixeerd (gekleurde accent-rand + vette glyph);
 * - `dimmed`  → de tegenovergestelde pool is gefixeerd (vergrijsd);
 * - `unfixed` → er is chart-context maar geen fixatie van deze pool (grijs
 *   driehoekje + standaard glyph — line-variant zonder oorzaak);
 * - `neutral` → geen chart-context (gewone gekleurde weergave — card-variant).
 */
export type HdGateLineDualityEmphasis = 'fixed' | 'dimmed' | 'unfixed' | 'neutral';

/**
 * Gedeelde presentational rij voor één exaltation/detriment van een lijn: planeet-glyph
 * (naam-tooltip) + richtingsdriehoekje + `keynoteWhite | keynoteBlack` met daaronder de
 * `~ customInterpretation` en de eigen `◆ ownInterpretation`. Elke bron is los aan/uit te
 * zetten ({@link showRa}, {@link showCustom}, {@link showOwn}). Pure component — alle data komt via {@link row}.
 */
@Component({
  selector: 'hd-gate-line-duality-row',
  templateUrl: './hd-gate-line-duality-row.component.html',
  styleUrl: './hd-gate-line-duality-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdOwnLineTextComponent],
})
export class HdGateLineDualityRowComponent {
  readonly row = input.required<HdGateLineDualityViewModel>();
  readonly kind = input.required<HdGateLineDualityKind>();
  /** Poort en lijn van deze rij: de reviewstatus van de eigen regel hangt eraan. */
  readonly gate = input<number>(0);
  readonly line = input<number>(0);
  /** Toon de `~ customInterpretation`-regel (alternatieve I Tjing-interpretatie).
   * De lijn- en card-variant sturen dit aan via de globale setting
   * "Alternatieve I Tjing-interpretaties tonen" (default uit). */
  readonly showCustom = input<boolean>(true);
  /** Toon de Ra-keynotes (White | Black Book); setting "Ra-lijnteksten", default aan. */
  readonly showRa = input<boolean>(true);
  /** Toon de eigen regel (◆, Mechanics in Motion); setting "eigen lijnteksten", default uit. */
  readonly showOwn = input<boolean>(false);
  /**
   * Visuele nadruk a.d.h.v. de fixatie-status van de lijn (default `neutral`).
   * De card laat dit op `neutral` (geen chart-context); de horizontale lijn-variant
   * stuurt het aan vanuit hover + default-fixatie-logica.
   */
  readonly emphasis = input<HdGateLineDualityEmphasis>('neutral');
  /**
   * Tooltip-tekst voor de planeet-glyph + het richtingsdriehoekje wanneer er een
   * fixatie-oorzaak is. Leeg → toon enkel de planeetnaam als glyph-tooltip
   * (default-gedrag). Verschijnt bij hover op zowel het glyph als de driehoek.
   */
  readonly fixationCauseTooltip = input<string>('');
  readonly fixationOrigins = input<readonly HdGateLineActivationOrigin[]>([]);
}
